import { Unit } from "config/enums/units";

const UNITS_STORAGE_KEY = "units";

/** Countries that use imperial units: the United States, Liberia, Myanmar and the United Kingdom (miles on roads) */
const IMPERIAL_COUNTRIES = ["US", "LR", "MM", "GB"];

/** Time zones of these countries (IANA names, incl. the old aliases some browsers still return) */
const IMPERIAL_TIME_ZONES = new Set([
  // United States
  "America/New_York",
  "America/Detroit",
  "America/Chicago",
  "America/Menominee",
  "America/Denver",
  "America/Boise",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Juneau",
  "America/Sitka",
  "America/Metlakatla",
  "America/Yakutat",
  "America/Nome",
  "America/Adak",
  "Pacific/Honolulu",
  "America/Indianapolis",
  "America/Fort_Wayne",
  "America/Louisville",
  "America/Knox_IN",
  "America/Shiprock",
  "America/Atka",
  "Navajo",
  // Liberia
  "Africa/Monrovia",
  // Myanmar
  "Asia/Yangon",
  "Asia/Rangoon",
  // United Kingdom, incl. the Crown Dependencies, which also use miles
  "Europe/London",
  "Europe/Belfast",
  "GB",
  "GB-Eire",
  "Europe/Guernsey",
  "Europe/Jersey",
  "Europe/Isle_of_Man",
]);

/** Groups of United States time zones, e.g. America/Indiana/Indianapolis or US/Eastern */
const IMPERIAL_TIME_ZONE_PREFIXES = [
  "US/",
  "America/Indiana/",
  "America/Kentucky/",
  "America/North_Dakota/",
];

const isUnit = (value: unknown): value is Unit =>
  Object.values(Unit).includes(value as Unit);

const getTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/** The region of the browser's language, e.g. "US" of "en-US" (not guessed from the language alone) */
const getLanguageRegion = () => {
  try {
    const language = navigator.languages?.[0] ?? navigator.language;
    return new Intl.Locale(language).region ?? null;
  } catch {
    return null;
  }
};

/**
 * The units used where the user is. The location comes from the device's time zone, which the system sets
 * by the location, so the browser doesn't ask for it. Without a time zone the region of the browser's language is used.
 */
export const detectUnits = (): Unit => {
  const timeZone = getTimeZone();
  if (timeZone) {
    const isImperial =
      IMPERIAL_TIME_ZONES.has(timeZone) ||
      IMPERIAL_TIME_ZONE_PREFIXES.some((prefix) => timeZone.startsWith(prefix));
    return isImperial ? Unit.imperial : Unit.metric;
  }

  const region = getLanguageRegion();
  return region && IMPERIAL_COUNTRIES.includes(region)
    ? Unit.imperial
    : Unit.metric;
};

/** The units chosen in this browser (in the settings or by the account the user signed in to) */
export const getLocalStorageUnits = (): Unit | null => {
  try {
    const units = localStorage.getItem(UNITS_STORAGE_KEY);
    return isUnit(units) ? units : null;
  } catch {
    return null;
  }
};

/** Keeps the units in this browser, so they're used after signing out and on the next visit */
export const storeUnits = (units: unknown) => {
  if (!isUnit(units)) return;
  try {
    localStorage.setItem(UNITS_STORAGE_KEY, units);
  } catch {
    // Storage blocked (e.g. private mode), the units are detected again next time
  }
};

/** Like the language: the units stored in this browser, otherwise the ones used where the user is */
export const getAutomaticUnits = (): Unit =>
  getLocalStorageUnits() ?? detectUnits();
